import {FC, useEffect, useState} from 'react';

import { Page } from '@/components/Page.tsx';
import {useWishlist} from "@/providers/WishlistProvider.tsx";
import {
  Button, ButtonCell, Caption,
  Headline, Modal,
  Section,
} from "@telegram-apps/telegram-ui";
import {Icon28AddCircle} from "@telegram-apps/telegram-ui/dist/icons/28/add_circle";
import {useParams, useOutletContext} from "react-router-dom";
import {CoverHeader} from "@/components/Wishlist/CoverHeader.tsx";
import {WishCell} from "@/components/Wish/WishCell.tsx";
import {useAuthUser} from "@/providers";
import {SubscribeButton} from "@/components/Wishlist/SubscribeButton.tsx";
import {TbPencil, TbShare3} from "react-icons/tb";
import {AuthProfile, Wish} from "@/api/types.ts";
import {shareLink} from "@/helpers/shareLink.ts";
import {
  ModalHeader
} from "@telegram-apps/telegram-ui/dist/components/Overlays/Modal/components/ModalHeader/ModalHeader";
import {WishView} from "@/components/Wish/WishView.tsx";
import {
  miniApp,
  useSignal
} from "@telegram-apps/sdk-react";
import {useNavigation} from "@/providers/NavigationProvider.tsx";
import {useTranslation} from "react-i18next";
import {useAuthProfile} from "@/providers/AuthProfileProvider.tsx";
import {applyWishOrder, buildUpdatedWishlistInfo} from "@/helpers/wishOrdering.ts";
import {ReorderControls} from "@/components/Wishlist/ReorderControls.tsx";
import {updateWishlist} from "@/api/apiMethods.ts";
import {CgReorder} from "react-icons/cg";
import {Recos} from "@/components/Idea/Recos.tsx";
import {WishActionsMenu} from "@/components/Wish/WishActionsMenu.tsx";
import {haptic} from "@/helpers/haptic.ts";

export const WishlistPage: FC = () => {
  const { wishlist, setWishlist } = useWishlist()
  const { authUser } = useAuthUser();
  const { wishId } = useParams();
  const { navigateToRoute, showSnackbar } = useNavigation();
  const [ openedWish, setOpenedWish ] = useState<Wish | null>(null);
  const isDark = useSignal(miniApp.isDark);
  const [ isBottomSheetOpen, setIsBottomSheetOpen ] = useState<boolean>(wishId != null);
  const { t } = useTranslation();
  const { refreshWishlist } = useOutletContext<{refreshWishlist: () => Promise<void>}>();

  // Reorder mode state management
  const [isReorderMode, setIsReorderMode] = useState<boolean>(false);
  const [tempWishOrder, setTempWishOrder] = useState<Wish[]>([]);
  const [originalWishOrder, setOriginalWishOrder] = useState<Wish[]>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  let authProfileFromContext: AuthProfile | undefined = undefined;
  try {
    const { authProfile } = useAuthProfile();
    authProfileFromContext = authProfile
  } catch (e) { /* empty */ }

  if (!wishlist) {
    throw new Error("WishlistPage must be inside the WishlistProvider");
  }

  useEffect(()=>{
    if (wishId) {
      const wish = wishlist.wishes.find(wish => wish.id === wishId);
      if (wish) {
        setOpenedWish(wish);
        return () => {setOpenedWish(null)};
      } else {
        if (isBottomSheetOpen) {
          setIsBottomSheetOpen(false);
        }
        let prefix = "";
        if (authProfileFromContext) {
          prefix = "/main";
        }
        navigateToRoute(`${prefix}/wishlists/${wishlist.id}`, true);
      }
    }
  }, [wishId, wishlist]);

  // Automatic reorder mode exit on navigation/unmount
  useEffect(() => {
    return () => {
      if (isReorderMode) {
        exitReorderMode();
      }
    };
  }, [isReorderMode]);

  const bottomSheetOpenChange = (isOpen: boolean) => {
    setIsBottomSheetOpen(isOpen);
    let prefix = "";
    if (authProfileFromContext) {
      prefix = "/main";
    }
    if (!isOpen) {
      navigateToRoute(`${prefix}/wishlists/${wishlist.id}`, true);
    } else {
      navigateToRoute(`${prefix}/wishlists/${wishlist.id}/wishes/${openedWish?.id}`, true);
    }
  }

  const openWish = (wish: Wish) => {
    haptic.impact('soft');
    setOpenedWish(wish);
    setIsBottomSheetOpen(true);
  }

  const onWishEditClick = (wish: Wish | null) => {
    if (!wish) {
      return;
    }
    navigateToRoute(`/main/wishlists/${wishlist.id}/wishes/${wish.id}/edit`);
  }

  let isOwner = true;
  if (!authUser || authUser.id !== wishlist.owner.id) {
    isOwner = false;
  }

  const share = async () => {
    haptic.impact('soft');
    const wishlistLink = import.meta.env.VITE_MINIAPP_URL + "?startapp=-w" + wishlist.shortId;
    await shareLink(wishlistLink);
  }

  const onWishUpdate = (wish: Wish) => {
    const index = wishlist.wishes.findIndex(w => w.id === wish.id);
    wishlist.wishes[index] = wish;
    setWishlist({...wishlist});
    setOpenedWish(wish);
    haptic.notification("success");
  }

  const onWishDelete = (wishId: string) => {
    const wishes = wishlist.wishes.filter(w => w.id !== wishId);
    haptic.notification("success");
    setWishlist({...wishlist, wishes});
    setOpenedWish(null);
    setIsBottomSheetOpen(false);

    let prefix = "";
    if (authProfileFromContext) {
      prefix = "/main";
    }
    navigateToRoute(`${prefix}/wishlists/${wishlist.id}`, true);
  }

  // Reorder mode functions
  const enterReorderMode = () => {
    haptic.impact('medium');
    const currentOrder = applyWishOrder(wishlist.wishes, wishlist.wishIds || []);
    setOriginalWishOrder([...currentOrder]);
    setTempWishOrder([...currentOrder]);
    setIsReorderMode(true);
  };

  const exitReorderMode = () => {
    setIsReorderMode(false);
    setTempWishOrder([]);
    setOriginalWishOrder([]);
    setIsSaving(false);
  };

  // Wish movement functions
  const moveWishUp = (index: number) => {
    if (index <= 0) return; // Boundary check - can't move up from top position
    
    const newOrder = [...tempWishOrder];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    setTempWishOrder(newOrder);
  };

  const moveWishDown = (index: number) => {
    if (index >= tempWishOrder.length - 1) return; // Boundary check - can't move down from bottom position
    
    const newOrder = [...tempWishOrder];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    setTempWishOrder(newOrder);
  };

  // Save functionality
  const saveReorder = async () => {
    if (!wishlist || isSaving) return;
    
    setIsSaving(true);
    try {
      // Build updated WishlistInfo with new wishIds
      const updatedWishlistInfo = buildUpdatedWishlistInfo(wishlist, tempWishOrder);
      
      // Call existing updateWishlist API method
      await updateWishlist(wishlist.id, updatedWishlistInfo);
      
      // Update local wishlist state to reflect new order
      const updatedWishlist = {
        ...wishlist,
        wishIds: updatedWishlistInfo.wishIds,
        wishes: tempWishOrder
      };
      setWishlist(updatedWishlist);
      haptic.notification('success');
      
      // Exit reorder mode
      exitReorderMode();
    } catch (error) {
      haptic.notification('error');
      // Display error message and remain in reorder mode
      showSnackbar('❌', 'system.reorderSaveFailed');
      setIsSaving(false);
    }
  };

  // Cancel functionality
  const cancelReorder = () => {
    // Restore original wish order
    setTempWishOrder([...originalWishOrder]);
    // Exit reorder mode without making API calls
    exitReorderMode();
  };

  let maxSizeCoverUrl = "";
  if (wishlist.cover) {
    maxSizeCoverUrl = wishlist.cover?.sizes[wishlist.cover.sizes.length-1]?.link;
  }

  return (
    <Page>
      <div className={"mt-1"}>
        <CoverHeader imageUrl={maxSizeCoverUrl}
                     title={wishlist.title}
                     description={wishlist.description}
                     fillGradient={true} />
      </div>
      <Section>
        <div className={"flex justify-between gap-3 p-3 pt-5 pb-5"}>
          {!isOwner && <SubscribeButton />}

          {isOwner && <Button mode={"bezeled"}
                              stretched={true}
                              onClick={() => {
                                navigateToRoute(`/main/wishlists/${wishlist.id}/edit`);
                              }}
                              before={<TbPencil size={22}/>}>
            {t('action.edit')}
          </Button>}

          <Button after={<TbShare3 size={22} />}
                  onClick={share}
                  className={isOwner ? "shiny-btn-primary" : ""}
                  stretched={true} mode={"filled"}>{t('action.share')}</Button>
        </div>
      </Section>
      <Section className={"mb-3"}>
        <div className={"flex justify-between p-2 pl-5 pr-0 min-h-10 items-center"}>
          <Headline weight="1">{t('wish.wishes')}</Headline>
          {isOwner && <Button
            onClick={() => {
              navigateToRoute(`/main/wishlists/${wishlist.id}/wishes/new`);
            }}
            after={<Icon28AddCircle></Icon28AddCircle>}
            mode={"plain"} size={"s"}>
            {t('action.add')}
          </Button>}
        </div>

        {(isReorderMode ? tempWishOrder : applyWishOrder(wishlist.wishes, wishlist.wishIds || [])).map((wish, index) => (
          <WishCell key={"wish_" + wish.id}
                    wish={wish}
                    onClick={() => openWish(wish)}
                    isReorderMode={isReorderMode}
                    canMoveUp={isReorderMode && index > 0}
                    canMoveDown={isReorderMode && index < tempWishOrder.length - 1}
                    onMoveUp={() => moveWishUp(index)}
                    onMoveDown={() => moveWishDown(index)}/>
        ))}

        {wishlist.wishes.length === 0 && <div className={"p-5 text-center"}>
          <Caption className={"text-color-hint"}>
            {t('state.emptyWishes')}
          </Caption>
        </div>}

        {isOwner && !isReorderMode && wishlist.wishes.length > 0 && <ButtonCell
          onClick={enterReorderMode}
          className={"pl-8"}
          before={<CgReorder size={28}/>}>
          <div>{t('action.reorder')}</div>
        </ButtonCell>}

        {isOwner && !isReorderMode && <ButtonCell
          onClick={() => {
            navigateToRoute(`/main/wishlists/${wishlist.id}/wishes/new`);
          }}
          className={"mb-3 pl-7"}
          before={<Icon28AddCircle/>}>
          <div className={"pl-1"}>{t('wish.action.add')}</div>
        </ButtonCell>}
      </Section>

      {isReorderMode && (
        <Section>
          <ReorderControls
            onSave={saveReorder}
            onCancel={cancelReorder}
            isSaving={isSaving}
          />
        </Section>
      )}

      {isOwner && authProfileFromContext && <Recos ideas={authProfileFromContext.ideas} />}

      <Modal
        header={<ModalHeader
          after={<WishActionsMenu
            wish={openedWish}
            isOwner={isOwner}
            onUpdate={onWishUpdate}
            onEditClick={() => onWishEditClick(openedWish)}
            onDelete={onWishDelete}
            refreshWishlist={refreshWishlist}
            onCloseWish={() => setIsBottomSheetOpen(false)}
          />}
        >
          {openedWish?.title || 'wish not loaded'}
        </ModalHeader>}
        open={isBottomSheetOpen}
        onOpenChange={bottomSheetOpenChange}
        className={"min-h-96"}
        style={{
          backgroundColor: (isDark ? "var(--tgui--section_bg_color)" : "var(--tgui--secondary_bg_color)"),
          maxHeight: "calc(100% - 50px)",
        }}
      >
        <WishView wish={openedWish} onUpdate={onWishUpdate} refreshWishlist={refreshWishlist} onCloseWish={() => setIsBottomSheetOpen(false)}/>
      </Modal>
    </Page>
  );
};
